// RTS input scheme: selection (click + box), move/attack/rally commands,
// build placement, zoom-to-cursor, panning (drag + keys), and UI hotkeys.
// All mouse math is in CSS pixels; world coords are hexToPixel(q, r, HEX_SIZE)
// world-px, scaled by cam.zoom on screen.

import { BUILDINGS, buildingFootprint, isBoxOrder, isPaintable } from '../shared/constants'
import { hexToPixel, pixelToHex } from '../shared/hex'
import type { ClientMsg } from '../shared/protocol'
import type { Building, UnitWire } from '../shared/types'
import { HEX_SIZE } from './state'
import type { GameState } from './state'
import { assignUnitSlots } from './slots'
import { pickEntity } from './pick'
import { resolvePier } from './pier'
import { rampDir } from './ramp'
import { GROUND_TILT, hexAtWorld, viewRotate, viewUnrotate } from './render/terrain'
import { paintBegin, paintCancel, paintCommit, paintExtend } from './paint'
import { boxOrderCells, boxOrderCommit, orderClick } from './chopBox'

const MIN_ZOOM = 0.25
const MAX_ZOOM = 6
/** Mouse travel (CSS px) before a LMB press becomes a box-select drag. */
const DRAG_THRESHOLD_PX = 5
/** Unit hit-test radius in world px ("within 0.8 hex of cursor"). */
const PICK_RADIUS = 0.8 * HEX_SIZE
/** Keyboard pan speed in screen px per second (world speed scales by 1/zoom). */
const PAN_SPEED = 640

/** A unit that fights on command: a citizen holding a weapon (or a legacy soldier type). */
export function isSoldier(u: { type: string; person?: { held?: string } }): boolean {
  if (u.type === 'citizen') {
    const h = u.person?.held
    return h === 'spear' || h === 'sword' || h === 'bow'
  }
  return u.type === 'spearman' || u.type === 'archer'
}

const PAN_KEYS: Record<string, 'up' | 'down' | 'left' | 'right'> = {
  w: 'up',
  arrowup: 'up',
  s: 'down',
  arrowdown: 'down',
  a: 'left',
  arrowleft: 'left',
  d: 'right',
  arrowright: 'right',
}

export function screenToWorld(
  st: GameState,
  canvas: HTMLCanvasElement,
  sx: number,
  sy: number,
): { x: number; y: number } {
  const z = st.cam.zoom
  const tilt = st.hasElev && st.lift25 ? GROUND_TILT : 1
  // Screen-aligned (rotated) world point, anchored at the camera's rotated position —
  // consumed by hexAtWorld / unitWorldPos, which both work in the spun frame.
  const cam = viewRotate(st, st.cam.x, st.cam.y)
  return {
    x: (sx - canvas.clientWidth / 2) / z + cam.x,
    y: (sy - canvas.clientHeight / 2) / (z * tilt) + cam.y,
  }
}

export function worldToScreen(
  st: GameState,
  canvas: HTMLCanvasElement,
  wx: number,
  wy: number,
): { x: number; y: number } {
  const z = st.cam.zoom
  const tilt = st.hasElev && st.lift25 ? GROUND_TILT : 1
  return {
    x: (wx - st.cam.x) * z + canvas.clientWidth / 2,
    y: (wy - st.cam.y) * z * tilt + canvas.clientHeight / 2,
  }
}

/**
 * Interpolated world position of a unit (fq,fr -> q,r by mp), plus its per-hex
 * slot offset — matching where the renderer actually draws it, so clicks land on
 * the right figure when several units share a hex. `slot` comes from
 * assignUnitSlots (in hex-radius units); pass it when hit-testing a crowd.
 */
function unitWorldPos(st: GameState, u: UnitWire, slot?: { dx: number; dy: number }): { x: number; y: number } {
  const a = hexToPixel(u.fq, u.fr, HEX_SIZE)
  const b = hexToPixel(u.q, u.r, HEX_SIZE)
  const t = Math.max(0, Math.min(1, u.mp))
  // Match the renderer's screen-aligned unit position: spin the interpolated tile
  // point, then add the slot spread as a screen-space offset (slots arrange sprites
  // on screen, so they don't rotate with the world).
  const p = viewRotate(st, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t)
  const ox = slot ? slot.dx * HEX_SIZE : 0
  const oy = slot ? slot.dy * HEX_SIZE : 0
  return { x: p.x + ox, y: p.y + oy }
}

export function initInput(
  canvas: HTMLCanvasElement,
  st: GameState,
  send: (m: ClientMsg) => void,
): void {
  const anySt = st as any
  anySt.selBox = null // { x0, y0, x1, y1 } in screen px while box-selecting, else null

  const panKeysHeld = new Set<'up' | 'down' | 'left' | 'right'>()
  let spaceHeld = false
  // Paint orders: LMB held in a paintable build mode (road / dig / fill) lays one
  // on each hex the cursor crosses.
  let painting = false
  let panning = false
  let panLast = { x: 0, y: 0 } // clientX/Y
  let lmbDown = false
  let boxing = false
  // Box orders (chop + terraform) use a rubber-band BOX (not a paint path): drag a
  // rectangle and every eligible hex inside it is selected. See chopBox.ts.
  let boxOrdering = false
  let downPos = { x: 0, y: 0 } // canvas-local CSS px

  function localXY(e: MouseEvent): { x: number; y: number } {
    const r = canvas.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }

  function hexAt(sx: number, sy: number): { q: number; r: number } {
    const w = screenToWorld(st, canvas, sx, sy)
    return hexAtWorld(st, w.x, w.y)
  }

  function pickUnit(sx: number, sy: number, ownOnly: boolean): { u: UnitWire; d: number } | null {
    const w = screenToWorld(st, canvas, sx, sy)
    // Hit-test against the exact positions the renderer drew each unit at (which
    // include smoothing + per-hex slot offset), so a click lands on the figure you
    // actually see. Fall back to a computed position for any unit not yet drawn.
    const drawn = st.unitPos
    let slots: Map<number, { dx: number; dy: number }> | null = null
    const cands: { u: UnitWire; d: number; y: number }[] = []
    for (const u of st.units.values()) {
      if (ownOnly && u.owner !== st.me) continue
      let p = drawn.get(u.id)
      if (!p) {
        if (!slots) slots = assignUnitSlots(st.units.values())
        p = unitWorldPos(st, u, slots.get(u.id))
      }
      const d = Math.hypot(p.x - w.x, p.y - w.y)
      if (d <= PICK_RADIUS) cands.push({ u, d, y: p.y })
    }
    if (cands.length === 0) return null
    // Nearest wins; among near-equal distances (overlapping sprites) prefer the
    // front-most (larger y, drawn on top) — the one visually under the cursor.
    const TIE = HEX_SIZE * 0.18
    cands.sort((a, b) => (Math.abs(a.d - b.d) > TIE ? a.d - b.d : b.y - a.y))
    return { u: cands[0].u, d: cands[0].d }
  }

  function buildingAt(q: number, r: number): Building | null {
    for (const b of st.buildings.values()) {
      for (const c of buildingFootprint(b.type, b.q, b.r)) if (c.q === q && c.r === r) return b
    }
    return null
  }

  function isHostileOwner(owner: string): boolean {
    if (!st.me || owner === st.me) return false
    const mine = st.factions.get(st.me)
    return !mine || !mine.allies.includes(owner)
  }

  function clearSelBox(): void {
    boxing = false
    anySt.selBox = null
  }

  function typing(): boolean {
    const el = document.activeElement as HTMLElement | null
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
  }

  /** Send the build command for the current buildMode at hex (q,r). The pier
   *  sends the SNAPPED anchor + chosen facing so the server places exactly what
   *  the ghost previewed; with no legal pair around the click the raw hex goes
   *  out anyway so the server's error toast explains why. */
  function sendBuild(q: number, r: number): void {
    if (!st.buildMode) return
    if (st.buildMode === 'pier') {
      const plan = resolvePier(st, q, r, st.pierRotate)
      if (plan) {
        send({ t: 'build', type: 'pier', q: plan.q, r: plan.r, dir: plan.dir })
        return
      }
    }
    if (st.buildMode === 'ramp') {
      const dir = rampDir(st, q, r, st.pierRotate)
      if (dir !== null) {
        send({ t: 'build', type: 'ramp', q, r, dir })
        return
      }
    }
    if (st.buildMode === 'farm') {
      send({ t: 'build', type: 'farm', q, r, dir: ((st.pierRotate % 6) + 6) % 6 })
      return
    }
    send({ t: 'build', type: st.buildMode, q, r })
  }

  // ---- Selection ------------------------------------------------------------

  function selectOwn(u: UnitWire, additive: boolean): void {
    // Own unit → selectable for commands, with additive (shift) toggling.
    if (additive && st.selection.units.length) {
      const ids = st.selection.units.includes(u.id)
        ? st.selection.units.filter((id) => id !== u.id)
        : [...st.selection.units, u.id]
      st.selection = { units: ids, building: null }
    } else {
      st.selection = { units: [u.id], building: null }
    }
  }

  // Double-click bookkeeping: a second click on the same unit kind within the
  // window selects every visible unit of that kind (serfs with serfs, soldiers
  // with soldiers).
  let lastClickAt = 0
  let lastClickUnit = -1

  function kindKey(u: { type: string; person?: { held?: string } }): string {
    return u.type === 'citizen' ? (isSoldier(u) ? 'soldier' : 'serf') : u.type
  }

  function selectAllOfKind(sample: { type: string; person?: { held?: string } }): void {
    const key = kindKey(sample)
    const a = screenToWorld(st, canvas, 0, 0)
    const b = screenToWorld(st, canvas, canvas.clientWidth, canvas.clientHeight)
    const ids: number[] = []
    for (const u of st.units.values()) {
      if (u.owner !== st.me || kindKey(u) !== key) continue
      const p = unitWorldPos(st, u)
      if (p.x >= a.x && p.x <= b.x && p.y >= a.y && p.y <= b.y) ids.push(u.id)
    }
    if (ids.length) st.selection = { units: ids, building: null }
  }

  function clickSelect(sx: number, sy: number, additive: boolean): void {
    // Select exactly what the cursor sits on: the topmost unit whose sprite covers
    // the point, else the building on that hex, else clear. Own units select for
    // commands (with shift toggling); foreign units/buildings are inspect-only.
    const hit = pickEntity(st, canvas, sx, sy)
    if (hit?.kind === 'unit') {
      const u = st.units.get(hit.id)
      if (!u) return
      const now = performance.now()
      if (u.owner === st.me && !additive && now - lastClickAt < 350 && lastClickUnit === u.id) {
        // double-click: grab everyone of this kind on screen
        selectAllOfKind(u)
        lastClickAt = 0
        lastClickUnit = -1
        return
      }
      lastClickAt = now
      lastClickUnit = u.id
      if (u.owner === st.me) selectOwn(u, additive)
      else st.selection = { units: [u.id], building: null }
      return
    }
    lastClickUnit = -1
    if (hit?.kind === 'building') {
      st.selection = { units: [], building: hit.id }
      return
    }
    if (!additive) st.selection = { units: [], building: null }
  }

  function boxSelect(x0: number, y0: number, x1: number, y1: number, additive: boolean): void {
    const a = screenToWorld(st, canvas, Math.min(x0, x1), Math.min(y0, y1))
    const b = screenToWorld(st, canvas, Math.max(x0, x1), Math.max(y0, y1))
    const ids: number[] = additive ? st.selection.units.filter((id) => st.units.has(id)) : []
    for (const u of st.units.values()) {
      if (u.owner !== st.me || !isSoldier(u)) continue
      const p = unitWorldPos(st, u)
      if (p.x >= a.x && p.x <= b.x && p.y >= a.y && p.y <= b.y && !ids.includes(u.id)) ids.push(u.id)
    }
    st.selection = { units: ids, building: null }
  }

  // ---- Commands ---------------------------------------------------------------

  function rightClick(sx: number, sy: number): void {
    if (st.buildMode) {
      // RMB cancels build placement (Esc also works).
      painting = false
      boxOrdering = false
      clearSelBox()
      paintCancel(st) // drop any in-progress drag highlight
      st.buildMode = null
      return
    }
    const h = hexAt(sx, sy)

    if (st.selection.units.length) {
      // Only own units take orders; a foreign unit in the selection is inspect-only.
      const ids = st.selection.units.filter((id) => st.units.get(id)?.owner === st.me)
      if (!ids.length) return
      // Hostile entity under cursor? Nearest unit within 0.8 hex first, else building.
      let target: { kind: 'u' | 'b'; id: number } | null = null
      let tq = h.q
      let tr = h.r
      const u = pickUnit(sx, sy, false)?.u
      if (u && isHostileOwner(u.owner)) {
        target = { kind: 'u', id: u.id }
        tq = u.q
        tr = u.r
      } else {
        const b = buildingAt(h.q, h.r)
        if (b && isHostileOwner(b.owner)) target = { kind: 'b', id: b.id }
      }
      const hasMilitary = ids.some((id) => {
        const un = st.units.get(id)
        return !!un && isSoldier(un)
      })
      if (target && hasMilitary) {
        send({ t: 'cmd', ids, action: 'attack', q: tq, r: tr, target })
      } else {
        send({ t: 'cmd', ids, action: 'move', q: h.q, r: h.r })
      }
      return
    }

    if (st.selection.building !== null) {
      const b = st.buildings.get(st.selection.building)
      // Trainers rally their recruits; the town hall rallies newborn serfs.
      if (b && b.owner === st.me && (BUILDINGS[b.type].trains?.length || b.type === 'townhall')) {
        send({ t: 'rally', buildingId: b.id, q: h.q, r: h.r })
      }
    }
  }

  // ---- Mouse ------------------------------------------------------------------

  canvas.addEventListener('contextmenu', (e) => e.preventDefault())

  // ---- Touch: tap select · drag pan · pinch zoom · long-press command ----------
  // Mirrors the mouse verbs through the same closures. touch-action: none on the
  // canvas keeps the browser from scrolling/zooming the page instead.
  interface TouchPt { x: number; y: number }
  const tpts = new Map<number, TouchPt>()
  let tMode: 'none' | 'tap' | 'pan' | 'pinch' = 'none'
  let tStart: TouchPt = { x: 0, y: 0 }
  let tLast: TouchPt = { x: 0, y: 0 }
  let pinch0 = 0
  let pinchZoom0 = 1
  let lpTimer: ReturnType<typeof setTimeout> | null = null
  const clearLp = (): void => {
    if (lpTimer !== null) {
      clearTimeout(lpTimer)
      lpTimer = null
    }
  }
  const tXY = (t: Touch): TouchPt => {
    const r = canvas.getBoundingClientRect()
    return { x: t.clientX - r.left, y: t.clientY - r.top }
  }

  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault()
    for (const t of Array.from(e.changedTouches)) tpts.set(t.identifier, tXY(t))
    if (tpts.size === 1) {
      tMode = 'tap'
      tStart = tLast = tXY(e.changedTouches[0])
      clearLp()
      lpTimer = setTimeout(() => {
        // long-press = the right-click verb (move/attack/rally, cancel build)
        if (tMode !== 'tap') return
        tMode = 'none'
        if (navigator.vibrate) navigator.vibrate(15)
        rightClick(tStart.x, tStart.y)
      }, 450)
    } else if (tpts.size === 2) {
      clearLp()
      tMode = 'pinch'
      const [a, b] = [...tpts.values()]
      pinch0 = Math.hypot(a.x - b.x, a.y - b.y)
      pinchZoom0 = st.cam.zoom
    }
  }, { passive: false })

  canvas.addEventListener('touchmove', (e) => {
    e.preventDefault()
    for (const t of Array.from(e.changedTouches)) {
      if (tpts.has(t.identifier)) tpts.set(t.identifier, tXY(t))
    }
    if (tMode === 'tap') {
      const p0 = [...tpts.values()][0]
      if (p0 && Math.hypot(p0.x - tStart.x, p0.y - tStart.y) > 9) {
        tMode = 'pan'
        tLast = p0
        clearLp()
      }
      st.hover = hexAt(tStart.x, tStart.y) // build-ghost preview follows the finger
    } else if (tMode === 'pan' && tpts.size === 1) {
      const p0 = [...tpts.values()][0]
      // Drag delta is screen-space; un-spin it so a finger drag pans the view the way
      // it moves regardless of the Q/E rotation. (Identity at rot 0.)
      const d = viewUnrotate(st, (p0.x - tLast.x) / st.cam.zoom, (p0.y - tLast.y) / st.cam.zoom)
      st.cam.x -= d.x
      st.cam.y -= d.y
      tLast = p0
    } else if (tMode === 'pinch' && tpts.size >= 2) {
      const [a, b] = [...tpts.values()]
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      if (pinch0 > 0 && d > 0) {
        st.cam.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchZoom0 * (d / pinch0)))
      }
    }
  }, { passive: false })

  const touchDone = (e: TouchEvent): void => {
    e.preventDefault()
    for (const t of Array.from(e.changedTouches)) tpts.delete(t.identifier)
    if (tMode === 'tap' && tpts.size === 0) {
      clearLp()
      // a tap runs the left-click verb: place in build mode, else select. (Touch has
      // no drag-paint — the finger pans — so a tap just places one order and re-arms.)
      if (st.buildMode && isBoxOrder(st.buildMode)) {
        const h = hexAt(tStart.x, tStart.y)
        orderClick(st, send, st.buildMode, h.q, h.r) // tap = one hex (queue, or remove in the Remove sub-mode)
      } else if (st.buildMode) {
        const h = hexAt(tStart.x, tStart.y)
        sendBuild(h.q, h.r)
      } else {
        clickSelect(tStart.x, tStart.y, false)
      }
    }
    if (tpts.size === 0) {
      tMode = 'none'
      clearLp()
    } else if (tpts.size === 1 && tMode === 'pinch') {
      tMode = 'pan'
      tLast = [...tpts.values()][0]
    }
  }
  canvas.addEventListener('touchend', touchDone, { passive: false })
  canvas.addEventListener('touchcancel', touchDone, { passive: false })

  canvas.addEventListener('mousedown', (e) => {
    const { x, y } = localXY(e)
    if (e.button === 1 || (e.button === 0 && spaceHeld)) {
      e.preventDefault()
      // Starting a pan interrupts any in-progress left-drag box-select: cancel
      // it so the selection rectangle isn't stranded on screen.
      lmbDown = false
      clearSelBox()
      panning = true
      panLast = { x: e.clientX, y: e.clientY }
      return
    }
    if (e.button === 0) {
      if (st.buildMode) {
        // Box orders (chop + terraform): start a rubber-band box (highlights as you drag,
        // commits on release).
        if (isBoxOrder(st.buildMode)) {
          boxOrdering = true
          st.paintType = st.buildMode
          st.paintPath = []
          downPos = { x, y }
          return
        }
        // Paint orders (road / dig / fill): start a drag stroke that only HIGHLIGHTS
        // as you go and commits on release (paint.ts). Other builds place immediately.
        if (isPaintable(st.buildMode)) {
          const h = hexAt(x, y)
          painting = true
          paintBegin(st, st.buildMode, h.q, h.r)
        } else {
          const h = hexAt(x, y)
          sendBuild(h.q, h.r)
          if (!e.shiftKey) st.buildMode = null
        }
        return
      }
      lmbDown = true
      boxing = false
      downPos = { x, y }
      return
    }
    if (e.button === 2) rightClick(x, y)
  })

  window.addEventListener('mousemove', (e) => {
    const { x, y } = localXY(e)
    // Hover hex + raw cursor (null when the pointer leaves the canvas). The
    // renderer re-picks the hovered entity from st.mouse every frame so the
    // silhouette tracks units that walk under a still cursor.
    if (x >= 0 && y >= 0 && x <= canvas.clientWidth && y <= canvas.clientHeight) {
      st.hover = hexAt(x, y)
      st.mouse = { x, y }
    } else {
      st.hover = null
      st.mouse = null
    }

    if (boxOrdering && st.paintType) {
      // Stretch the box and re-select every eligible hex whose (spun) world position lies
      // inside it — matching the frame unitWorldPos / screenToWorld work in. Commit on up.
      anySt.selBox = { x0: downPos.x, y0: downPos.y, x1: x, y1: y }
      const a = screenToWorld(st, canvas, Math.min(downPos.x, x), Math.min(downPos.y, y))
      const b = screenToWorld(st, canvas, Math.max(downPos.x, x), Math.max(downPos.y, y))
      st.paintPath = boxOrderCells(st, st.paintType, (q, r) => {
        const wp = viewRotate(st, hexToPixel(q, r, HEX_SIZE).x, hexToPixel(q, r, HEX_SIZE).y)
        return wp.x >= a.x && wp.x <= b.x && wp.y >= a.y && wp.y <= b.y
      }, st.chopCancel)
      return
    }

    if (painting && st.buildMode && isPaintable(st.buildMode) && st.hover) {
      // Extend the highlighted stroke to the hovered hex; a back-drag trims the tail.
      // Nothing is sent until mouseup.
      paintExtend(st, st.hover.q, st.hover.r)
    }

    if (panning) {
      const dx = e.clientX - panLast.x
      const dy = e.clientY - panLast.y
      panLast = { x: e.clientX, y: e.clientY }
      const d = viewUnrotate(st, dx / st.cam.zoom, dy / st.cam.zoom) // un-spin the drag
      st.cam.x -= d.x
      st.cam.y -= d.y
      return
    }

    if (lmbDown) {
      if (!boxing && Math.hypot(x - downPos.x, y - downPos.y) > DRAG_THRESHOLD_PX) boxing = true
      if (boxing) anySt.selBox = { x0: downPos.x, y0: downPos.y, x1: x, y1: y }
    }
  })

  window.addEventListener('mouseup', (e) => {
    if (e.button === 0 && boxOrdering) {
      const type = st.paintType
      boxOrdering = false
      clearSelBox()
      const { x, y } = localXY(e)
      if (type && Math.hypot(x - downPos.x, y - downPos.y) > DRAG_THRESHOLD_PX) boxOrderCommit(st, send, type, st.chopCancel)
      else { const h = hexAt(x, y); if (type) orderClick(st, send, type, h.q, h.r); st.paintPath = []; st.paintType = null }
      return // tool stays armed for the next box
    }
    if (e.button === 0 && painting) {
      painting = false
      // Release commits the highlighted stroke at once (valid hexes only).
      paintCommit(st, send)
      // Shift keeps the tool armed for the next stroke; otherwise done.
      if (!e.shiftKey) st.buildMode = null
    }
    if (panning && (e.button === 1 || e.button === 0)) {
      panning = false
      if (e.button === 0) lmbDown = false
      return
    }
    if (e.button !== 0 || !lmbDown) return
    lmbDown = false
    const { x, y } = localXY(e)
    if (boxing) {
      clearSelBox()
      boxSelect(downPos.x, downPos.y, x, y, e.shiftKey)
      return
    }
    clickSelect(x, y, e.shiftKey)
  })

  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault()
      const { x, y } = localXY(e)
      const dy = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY
      const factor = Math.exp(-dy * 0.0015)
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, st.cam.zoom * factor))
      if (newZoom === st.cam.zoom) return
      // Keep the world point under the cursor fixed while zooming. before/after are
      // screen-aligned; un-spin their delta before nudging the (unrotated) camera.
      const before = screenToWorld(st, canvas, x, y)
      st.cam.zoom = newZoom
      const after = screenToWorld(st, canvas, x, y)
      const d = viewUnrotate(st, before.x - after.x, before.y - after.y)
      st.cam.x += d.x
      st.cam.y += d.y
    },
    { passive: false },
  )

  // ---- Keyboard ----------------------------------------------------------------

  window.addEventListener('keydown', (e) => {
    if (typing()) return
    const key = e.key
    const lower = key.toLowerCase()

    if (key === ' ') {
      spaceHeld = true
      e.preventDefault()
      return
    }
    const dir = PAN_KEYS[lower]
    if (dir) {
      panKeysHeld.add(dir)
      e.preventDefault()
      return
    }
    if (lower === 'b') {
      if (!st.observer) st.emit('ui:build')
      return
    }
    if (lower === 'f') {
      // Focus: centre the camera on the selection, else jump home.
      let tx: number | null = null
      let ty = 0
      if (st.selection.units.length) {
        let x = 0
        let y = 0
        let n = 0
        for (const id of st.selection.units) {
          const u = st.units.get(id)
          if (!u) continue
          const p = unitWorldPos(st, u)
          x += p.x
          y += p.y
          n++
        }
        if (n) {
          // unitWorldPos is screen-aligned; the camera stores unrotated world, so
          // un-spin the centroid before centring on it.
          const w = viewUnrotate(st, x / n, y / n)
          tx = w.x
          ty = w.y
        }
      } else if (st.selection.building !== null) {
        const b = st.buildings.get(st.selection.building)
        if (b) {
          const p = hexToPixel(b.q, b.r, HEX_SIZE)
          tx = p.x
          ty = p.y
        }
      }
      if (tx === null && st.home) {
        const p = hexToPixel(st.home.q, st.home.r, HEX_SIZE)
        tx = p.x
        ty = p.y
      }
      if (tx !== null) {
        st.cam.x = tx
        st.cam.y = ty
      }
      return
    }
    if (lower === 'q' || lower === 'e') {
      // Spin the view 60°. rot is the target; the renderer eases rotF toward it for a
      // smooth turn (and normalises to 0..5 once it lands). Unbounded ±1 so wrapping
      // 5→0 spins forward, not all the way back. Camera keeps its world focus.
      st.cam.rot = (st.cam.rot ?? 0) + (lower === 'e' ? 1 : -1)
      e.preventDefault()
      return
    }
    if (lower === 'h') {
      st.emit('ui:help')
      return
    }
    if (lower === 'l' && st.hasElev) {
      // Toggle the 2.5D height view (elevation worlds only). Persisted so a
      // player who prefers flat top-down keeps it across sessions.
      st.lift25 = !st.lift25
      try { localStorage.setItem('hexFlat', st.lift25 ? '0' : '1') } catch { /* private mode */ }
      return
    }
    if (lower === 'p') {
      st.emit('ui:diplo')
      return
    }
    if (lower === 'r') {
      // While placing a pier, R rotates the pending orientation through the
      // anchor's valid water sides (the ghost applies it modulo the count);
      // otherwise R keeps its roster binding.
      if (st.buildMode === 'pier' || st.buildMode === 'ramp' || st.buildMode === 'farm') st.pierRotate++
      else st.emit('ui:roster')
      return
    }
    if (key === 'Escape') {
      painting = false
      boxOrdering = false
      paintCancel(st) // drop any in-progress drag highlight
      if (st.buildMode) st.buildMode = null
      else st.selection = { units: [], building: null }
      lmbDown = false
      clearSelBox()
      return
    }
    if (key === 'Delete') {
      const id = st.selection.building
      if (id !== null) {
        const b = st.buildings.get(id)
        if (b && b.owner === st.me) {
          send({ t: 'demolish', id })
          st.selection = { units: [], building: null }
        }
      }
    }
  })

  window.addEventListener('keyup', (e) => {
    if (e.key === ' ') spaceHeld = false
    const dir = PAN_KEYS[e.key.toLowerCase()]
    if (dir) panKeysHeld.delete(dir)
  })

  window.addEventListener('blur', () => {
    panKeysHeld.clear()
    spaceHeld = false
    panning = false
    lmbDown = false
    boxOrdering = false
    clearSelBox()
    // A focus loss mid-drag means the window mouseup may never arrive; drop the stroke
    // so it can't keep painting under a button-less cursor (or commit stale on return).
    painting = false
    paintCancel(st)
  })

  // ---- Touch rotate chip --------------------------------------------------------
  // Touch devices have no R key: while a pier ghost is armed, a floating ⟳ chip
  // near the build dock cycles the pending orientation instead. (Chosen over
  // tap-again-to-rotate: a second tap already means "place", and overloading it
  // would double-place piers.) Visibility is synced every frame in panLoop.
  let rotateChip: HTMLButtonElement | null = null
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    rotateChip = document.createElement('button')
    rotateChip.className = 'pier-rotate-chip'
    rotateChip.textContent = '⟳ rotate'
    rotateChip.style.display = 'none'
    rotateChip.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      e.stopPropagation()
      st.pierRotate++
    })
    document.body.appendChild(rotateChip)
  }

  // Held-key panning: constant screen speed regardless of zoom.
  let lastT = performance.now()
  function panLoop(t: number): void {
    const dt = Math.min(0.1, (t - lastT) / 1000)
    lastT = t
    if (panKeysHeld.size && !typing()) {
      let dx = 0
      let dy = 0
      if (panKeysHeld.has('left')) dx -= 1
      if (panKeysHeld.has('right')) dx += 1
      if (panKeysHeld.has('up')) dy -= 1
      if (panKeysHeld.has('down')) dy += 1
      if (dx !== 0 || dy !== 0) {
        const n = Math.hypot(dx, dy)
        const speed = (PAN_SPEED / st.cam.zoom) * dt
        // Un-spin the screen-space direction so WASD/arrows pan by what you see.
        const d = viewUnrotate(st, (dx / n) * speed, (dy / n) * speed)
        st.cam.x += d.x
        st.cam.y += d.y
      }
    }
    if (rotateChip) rotateChip.style.display = st.buildMode === 'pier' || st.buildMode === 'ramp' || st.buildMode === 'farm' ? '' : 'none'
    requestAnimationFrame(panLoop)
  }
  requestAnimationFrame(panLoop)
}
